import { sessionStorage } from "@web-monorepo/safe-browser-storage";

export type ReferralRecipientDestinationHref = `/${string}`;
export function isReferralRecipientDestinationHref(value: unknown): value is ReferralRecipientDestinationHref {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//");
}

const STORAGE_KEY = "marketplaceParentReferralPendingDestination";

export interface ParentReferralPendingDestination {
  referralCode: string;
  destinationHref: ReferralRecipientDestinationHref;
  sourceHref: ReferralRecipientDestinationHref;
}

interface ReferralIntent {
  referralCode: string;
  destinationHref?: ReferralRecipientDestinationHref;
  sourceHref?: ReferralRecipientDestinationHref;
  authentication?: { userId?: string };
}

function loadIntent(): ReferralIntent | undefined {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || !("referralCode" in value) || typeof value.referralCode !== "string") {
      return undefined;
    }
    const intent = value as ReferralIntent;
    if (
      (intent.destinationHref !== undefined || intent.sourceHref !== undefined) &&
      (!isReferralRecipientDestinationHref(intent.destinationHref) ||
        !isReferralRecipientDestinationHref(intent.sourceHref))
    ) {
      return undefined;
    }
    if (!isValidAuthentication(intent.authentication)) return undefined;
    return intent;
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    return undefined;
  }
}

function saveIntent(intent: ReferralIntent) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
}

export function saveParentReferralPendingDestination(destination: ParentReferralPendingDestination) {
  const current = loadIntent();
  saveIntent({ ...(current?.referralCode === destination.referralCode ? current : {}), ...destination });
}

export function loadParentReferralPendingDestination(referralCode: string | undefined) {
  const intent = loadIntent();
  return referralCode && intent?.referralCode === referralCode && intent.destinationHref && intent.sourceHref
    ? { referralCode, destinationHref: intent.destinationHref, sourceHref: intent.sourceHref }
    : null;
}

export function clearParentReferralPendingDestination() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function beginParentReferralAuthentication(referralCode: string | undefined) {
  if (!referralCode) return;
  const current = loadIntent();
  saveIntent({ ...(current?.referralCode === referralCode ? current : {}), referralCode, authentication: {} });
}

export function markParentReferralAuthenticationComplete(userId: string) {
  const intent = loadIntent();
  if (intent?.authentication) saveIntent({ ...intent, authentication: { userId } });
}

export function consumeParentReferralAuthentication(referralCode: string | undefined, userId: string | undefined) {
  const intent = loadIntent();
  if (!userId || !intent || intent.referralCode !== referralCode || intent.authentication?.userId !== userId) {
    return false;
  }
  // Consuming feedback ownership must not erase the destination needed by completion or retry.
  saveIntent({ ...intent, authentication: undefined });
  return true;
}

function isValidAuthentication(authentication: unknown) {
  if (authentication === undefined) return true;
  if (!authentication || typeof authentication !== "object" || Array.isArray(authentication)) return false;
  return !("userId" in authentication) || typeof authentication.userId === "string";
}
